import { Hud } from '@chessmate/ui';
import { useMemo } from 'react';
import { gameNoun, levelLabel } from '../edition/index.js';
import { HEATMAP_MODE_I18N_KEYS } from '../i18n/heatmapModeKeys.js';
import { useT } from '../i18n/useT.js';
import { usePhone } from './useBoardCamera.js';
import { useGameStore } from '../state/game-store.js';
import { CoachPanel } from './CoachPanel.js';

export function GamePanel({ onExit }: { onExit: () => void }) {
  const { t: tCommon } = useT('common');
  const { t: tGames } = useT('games');
  const phone = usePhone();
  const pos = useGameStore((s) => s.pos);
  const adapter = useGameStore((s) => s.adapter);
  const level = useGameStore((s) => s.level);
  // Memoize: adapter.history() returns a fresh array each call; selecting it
  // directly would give useSyncExternalStore an unstable snapshot and loop.
  const moves = useMemo(() => adapter.history(pos), [adapter, pos]);
  const heatmap = useGameStore((s) => s.heatmap);
  const robotThinking = useGameStore((s) => s.robotThinking);
  const result = useGameStore((s) => s.pos.result);
  const coach = useGameStore((s) => s.coach);
  const coachLoading = useGameStore((s) => s.coachLoading);
  const phase = useGameStore((s) => s.phase);
  const isChess = adapter.gameId === 'chess';
  // Resolved here rather than inside `Hud`, which has no i18n dependency.
  const heatmapLabel = tGames(HEATMAP_MODE_I18N_KEYS[heatmap]);

  const extras = (
    <>
      {isChess && (
        <CoachPanel
          advice={coach}
          loading={coachLoading}
          onAsk={() => void useGameStore.getState().askCoach()}
          disabled={phase !== 'playing'}
        />
      )}
      {/* Checkers only: the one-line goal reminder for the promotion band.
          Sits where the coach panel would be in chess, so each game has
          exactly one helper card and neither carries dead space. */}
      {!isChess && (
        <p
          className="glass glass-light rounded-xl px-3 py-2 text-sm font-bold text-ink"
          data-testid="promotion-hint"
          data-appdom-id="promotion-hint"
          data-appdom-type="Text"
        >
          {tGames('promotion-band-hint')}
        </p>
      )}
    </>
  );

  return (
    <aside className="flex w-full flex-col gap-3 lg:w-80" data-testid="game-panel">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-extrabold text-berry">{level ? levelLabel(level) : 'Robot'} {gameNoun}</h1>
        <button
          type="button"
          className="inline-flex min-h-[48px] items-center rounded-lg px-3 font-bold text-accent underline underline-offset-2"
          onClick={onExit}
          data-testid="back-home"
        >
          {tCommon('home')}
        </button>
      </div>
      <Hud
        turn={pos.turn}
        captured={pos.captured}
        moves={moves}
        heatmapLabel={heatmapLabel}
        robotThinking={robotThinking}
        result={result}
        disabled={phase !== 'playing'}
        hideCoach={!isChess}
        onToggleHeatmap={() => useGameStore.getState().cycleHeatmap()}
        onCoach={() => void useGameStore.getState().askCoach()}
        onUndo={() => useGameStore.getState().undo()}
        onRestart={() => useGameStore.getState().restart()}
        compact={phone}
        moreLabel={tCommon('panel-more')}
      >
        {phone ? extras : null}
      </Hud>
      {!phone && extras}
    </aside>
  );
}
